// Async Analysis Service - Central service for RabbitMQ-based async analysis
import { httpClient } from './http.client';
import { planTypeFromName } from './plan-type';
import { KeycloakService } from './keycloak.service';
import { SubscriptionService } from './subscription.service';
import { API_BASE_URL } from './api.constants';
import type { AnalysisSession } from '../types/analysis-session';

const V3_BASE = `${API_BASE_URL}/agent-v3/api/v3`;

// Types

/** Raw response from backend analyze-async endpoints */
export interface AnalyzeAsyncResponse {
  success: boolean;
  async: boolean;
  data: {
    session_id: string;
    status?: string;
    media_type?: string;
    queued_components?: string[];
    poll_url?: string;
    result_url?: string;
    skipped?: boolean;           // URL extraction failed gracefully
    skip_reason?: string;        // e.g. "extraction_failed", "no_text_content"
    message?: string;            // human-readable skip message
  } & Partial<AnalysisSession>;     // when async: false, data IS the full AnalysisSession
  warnings?: string[];
  message?: string;
}

export type AnalysisComponent = 'pipeline' | 'techniques' | 'claims' | 'ai-tampered' | 'domain' | 'source-assessment';

class AsyncAnalysisService {
  /** Resolve plan_type from subscription */
  async getPlanType(): Promise<number> {
    try {
      const usage = await SubscriptionService.getUsageStats();
      return planTypeFromName(usage.plan.name);
    } catch {
      return 1;
    }
  }

  /** Get user_id from keycloak */
  getUserId(): string {
    return KeycloakService.getUserFromToken()?.sub || '';
  }

  /** Get user email from keycloak */
  getUserEmail(): string {
    return KeycloakService.getUserFromToken()?.email || '';
  }

  /** Submit text analysis (async) */
  async submitText(
    component: AnalysisComponent,
    text: string,
    extras?: Record<string, any>
  ): Promise<AnalyzeAsyncResponse> {
    const planType = await this.getPlanType();
    const endpoint = `${V3_BASE}/${component}/analyze-async`;
    const body = {
      text,
      media_type: 'text',
      user_id: this.getUserId(),
      user_email: this.getUserEmail(),
      plan_type: planType,
      ...extras,
    };
    return httpClient.post<AnalyzeAsyncResponse>(endpoint, body, { timeout: 60000 });
  }

  /** Submit URL analysis (async) */
  async submitUrl(
    component: AnalysisComponent,
    url: string
  ): Promise<AnalyzeAsyncResponse> {
    const planType = await this.getPlanType();
    const endpoint = `${V3_BASE}/${component}/analyze-async`;
    // Normalize www. prefix to https://
    const normalizedUrl = /^www\./i.test(url) ? `https://${url}` : url;
    const body = {
      url: normalizedUrl,
      media_type: 'url',
      user_id: this.getUserId(),
      user_email: this.getUserEmail(),
      plan_type: planType,
    };
    return httpClient.post<AnalyzeAsyncResponse>(endpoint, body, { timeout: 60000 });
  }

  /** Submit source assessment analysis (replaces legacy domain analysis) */
  async submitDomain(
    input: string
  ): Promise<AnalyzeAsyncResponse> {
    const planType = await this.getPlanType();
    const endpoint = `${V3_BASE}/source-assessment/analyze`;
    const isUrl = /^(https?:\/\/|www\.)/i.test(input);
    const normalizedUrl = /^www\./i.test(input) ? `https://${input}` : input;
    const body: Record<string, any> = {
      user_id: this.getUserId(),
      user_email: this.getUserEmail(),
      plan_type: planType,
    };
    if (isUrl) {
      body.url = normalizedUrl;
    } else {
      body.domain = input;
    }
    return httpClient.post<AnalyzeAsyncResponse>(endpoint, body, { timeout: 60000 });
  }

  /** Upload file to /api/v3/media/upload -> returns public_url */
  async uploadFile(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', this.getUserId());
    const res = await httpClient.post<{ success: boolean; data: { public_url: string } }>(
      `${V3_BASE}/media/upload`,
      formData,
      { timeout: 120000, retries: 0 }
    );
    return res.data.public_url;
  }

  /** Submit media (file) analysis -- 2 steps: upload file, then send to async endpoint */
  async submitMedia(
    component: AnalysisComponent,
    file: File,
    mediaType: string,
    contextText?: string,
    durationSec?: number
  ): Promise<AnalyzeAsyncResponse> {
    // Step 1: Upload file -> get public_url
    const publicUrl = await this.uploadFile(file);

    // Step 2: Send media_url to async endpoint (same as text/url)
    const planType = await this.getPlanType();
    const endpoint = `${V3_BASE}/${component}/analyze-async`;
    const body: Record<string, any> = {
      media_url: publicUrl,
      media_type: mediaType,
      user_id: this.getUserId(),
      user_email: this.getUserEmail(),
      plan_type: planType,
    };
    if (contextText) body.text = contextText;
    if (durationSec) body.media_duration_sec = durationSec;

    return httpClient.post<AnalyzeAsyncResponse>(endpoint, body, { timeout: 60000 });
  }

  /** Poll queue status — returns partial AnalysisSession with _queue metadata */
  async getQueueStatus(sessionId: string): Promise<AnalysisSession> {
    const res = await httpClient.get<{ success: boolean; data: AnalysisSession }>(
      `${V3_BASE}/pipeline/${sessionId}/queue-status`
    );
    return res.data;
  }

  /** Get final result — returns full AnalysisSession */
  async getResult(sessionId: string): Promise<{ success: boolean; data: AnalysisSession }> {
    return httpClient.get<{ success: boolean; data: AnalysisSession }>(
      `${V3_BASE}/pipeline/${sessionId}/result`
    );
  }

  // === Sync endpoints ===

  /** Sync standalone component analysis */
  async analyzeSync(
    component: 'techniques' | 'claims' | 'ai-tampered',
    body: Record<string, any>
  ): Promise<{ success: boolean; data: AnalysisSession }> {
    return httpClient.post<{ success: boolean; data: AnalysisSession }>(
      `${V3_BASE}/${component}/analyze`,
      body,
      { timeout: 120000 }
    );
  }

  /** Sync standalone component media analysis */
  async analyzeSyncMedia(
    component: 'techniques' | 'claims' | 'ai-tampered' | 'pipeline',
    body: Record<string, any>
  ): Promise<{ success: boolean; data: AnalysisSession }> {
    const endpoint = component === 'pipeline'
      ? `${V3_BASE}/pipeline/analyze-media`
      : `${V3_BASE}/${component}/analyze-media`;
    return httpClient.post<{ success: boolean; data: AnalysisSession }>(
      endpoint,
      body,
      { timeout: 300000 }
    );
  }

  /** Sync full pipeline analysis (text) */
  async analyzePipelineSync(
    body: Record<string, any>
  ): Promise<{ success: boolean; data: AnalysisSession }> {
    return httpClient.post<{ success: boolean; data: AnalysisSession }>(
      `${V3_BASE}/pipeline/analyze`,
      body,
      { timeout: 300000 }
    );
  }

  /** Sync full pipeline media analysis */
  async analyzePipelineMedia(
    body: Record<string, any>
  ): Promise<{ success: boolean; data: AnalysisSession }> {
    return httpClient.post<{ success: boolean; data: AnalysisSession }>(
      `${V3_BASE}/pipeline/analyze-media`,
      body,
      { timeout: 300000 }
    );
  }
}

export const asyncAnalysisService = new AsyncAnalysisService();
